import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useGameStore } from "../store/useGameStore";
import { useWorldBossStore, formatBossWindowLabel } from "../store/useWorldBossStore";
import { useGuildStore, palaceLabel } from "../store/useGuildStore";
import { DailyRewardModal } from "../components/DailyRewardModal";
import { ActiveEventRing, type ActiveEventVariant } from "../components/ActiveEventRing";
import { FormattedNumber } from "../components/FormattedNumber";
import { useArenaStore } from "../store/useArenaStore";
import { useMineStore } from "../store/useMineStore";
import { useRouletteStore } from "../store/useRouletteStore";
import { formatDisplayNumber } from "../utils/formatNumber";
import { formatDuration, formatDurationLong } from "../utils/format";
import type { IdleStatus } from "../types";

export function MainScreen() {
  const {
    hero,
    balance,
    setScreen,
    collectIdle,
    refreshBalance,
    dailyRewardStatus,
    refreshDailyReward,
    claimDailyReward,
  } = useGameStore();

  const { status: worldBossStatus, pollStatus: pollWorldBoss } = useWorldBossStore();
  const { overview: guildOverview, refresh: refreshGuild } = useGuildStore();
  const scheduleOpen = useArenaStore((s) => s.scheduleOpen);
  const fetchSchedule = useArenaStore((s) => s.fetchSchedule);
  const mineStatus = useMineStore((s) => s.status);
  const refreshMine = useMineStore((s) => s.refresh);
  const rouletteStatus = useRouletteStore((s) => s.status);
  const refreshRoulette = useRouletteStore((s) => s.refresh);

  const idleDismissedRef = useRef(false);
  const [showDailyPopup, setShowDailyPopup] = useState(false);
  const [showIdlePopup, setShowIdlePopup] = useState(false);
  const [idleModalPending, setIdleModalPending] = useState(0);
  const [claimingDaily, setClaimingDaily] = useState(false);
  const [dailyClaimed, setDailyClaimed] = useState(false);
  const [dailyClaimSummary, setDailyClaimSummary] = useState<string | null>(null);
  const [collecting, setCollecting] = useState(false);
  const [collectedAmount, setCollectedAmount] = useState(0);

  useEffect(() => {
    void refreshBalance();
  }, [refreshBalance]);

  useEffect(() => {
    void refreshDailyReward();
  }, [refreshDailyReward]);

  useEffect(() => {
    void pollWorldBoss();
    const t = setInterval(() => void pollWorldBoss(), 15000);
    return () => clearInterval(t);
  }, [pollWorldBoss]);

  useEffect(() => {
    void refreshGuild();
    const t = setInterval(() => void refreshGuild(), 60000);
    return () => clearInterval(t);
  }, [refreshGuild]);

  useEffect(() => {
    void fetchSchedule();
    const t = setInterval(() => void fetchSchedule(), 15000);
    return () => clearInterval(t);
  }, [fetchSchedule]);

  useEffect(() => {
    if ((hero?.level ?? 0) < 10) return;
    void refreshMine();
    const t = setInterval(() => void refreshMine(), 30000);
    return () => clearInterval(t);
  }, [hero?.level, refreshMine]);

  useEffect(() => {
    void refreshRoulette();
    const t = setInterval(() => void refreshRoulette(), 30000);
    return () => clearInterval(t);
  }, [refreshRoulette]);

  useEffect(() => {
    if (dailyRewardStatus?.can_claim) {
      setDailyClaimed(false);
      setDailyClaimSummary(null);
      setShowDailyPopup(true);
    }
  }, [dailyRewardStatus?.can_claim, dailyRewardStatus?.streak_day]);

  useEffect(() => {
    if (!balance || balance.idle_gold_pending <= 0) {
      idleDismissedRef.current = false;
      return;
    }
    if (showDailyPopup || idleDismissedRef.current) return;

    setIdleModalPending(balance.idle_gold_pending);
    setShowIdlePopup(true);
  }, [balance?.idle_gold_pending, showDailyPopup]);

  const handleDailyClaim = async () => {
    setClaimingDaily(true);
    try {
      const result = await claimDailyReward();
      const parts: string[] = [];
      if (result.gold > 0) parts.push(`+${formatDisplayNumber(result.gold)} gold`);
      if (result.diamonds > 0) parts.push(`+${formatDisplayNumber(result.diamonds)} diamonds`);
      if (result.newbie_bonus_gold && result.newbie_bonus_gold > 0) {
        parts.push(`+${formatDisplayNumber(result.newbie_bonus_gold)} gold (new hero)`);
      }
      if (result.newbie_bonus_diamonds && result.newbie_bonus_diamonds > 0) {
        parts.push(`+${formatDisplayNumber(result.newbie_bonus_diamonds)} diamonds (new hero)`);
      }
      if (result.consumable_item_id) parts.push(`${result.consumable_item_id} ×${result.consumable_quantity}`);
      if (result.item_template_name) parts.push(result.item_template_name);
      setDailyClaimSummary(parts.join(", ") || "Rewards added to your account.");
      setDailyClaimed(true);
    } finally {
      setClaimingDaily(false);
    }
  };

  const closeDailyPopup = () => {
    setShowDailyPopup(false);
    const pending = useGameStore.getState().balance?.idle_gold_pending ?? 0;
    if (pending > 0 && !idleDismissedRef.current) {
      setIdleModalPending(pending);
      setShowIdlePopup(true);
    }
  };

  const dismissIdleEntryModal = () => {
    idleDismissedRef.current = true;
    setShowIdlePopup(false);
    setIdleModalPending(0);
    setCollectedAmount(0);
  };

  const handleCollect = async () => {
    setCollecting(true);
    const amount = idleModalPending || balance?.idle_gold_pending || 0;
    const collected = await collectIdle();
    idleDismissedRef.current = false;
    setCollectedAmount(collected > 0 ? collected : amount);
    setCollecting(false);
    setTimeout(() => {
      setShowIdlePopup(false);
      setIdleModalPending(0);
      setCollectedAmount(0);
    }, 1200);
  };

  const bossActive = worldBossStatus?.is_active && !worldBossStatus.boss_dead;
  const partyArenaActive = scheduleOpen;
  const mineEventActive = (hero?.level ?? 0) >= 10 && (mineStatus?.daily?.event_active ?? false);
  const rouletteActive = rouletteStatus?.active ?? false;

  return (
    <div className="p-4 space-y-4">
      {/* Title */}
      <div className="text-center py-2">
        <h1 className="text-2xl font-bold font-game text-gold-400 drop-shadow">
          ⚔️ Meltown Kingdoms
        </h1>
      </div>

      {rouletteActive && (
        <div className="relative">
          <ActiveEventRing variant="amber" />
          <button
            type="button"
            onClick={() => setScreen("roulette")}
            className={clsx(
              "relative z-0 w-full rounded-xl p-4 text-left shadow-lg active:scale-[0.98] transition-all",
              "bg-gradient-to-r from-amber-700 via-orange-700 to-amber-900",
              "border border-amber-500/40 shadow-[0_0_20px_rgba(251,191,36,0.12)]",
            )}
          >
            <div className="flex items-center gap-3">
              <span className="text-4xl">🎡</span>
              <div>
                <div className="font-bold text-white font-game text-lg">Fortune Wheel</div>
                <div className="text-amber-100/80 text-xs">
                  Event active — spin for prizes!
                  {rouletteStatus && rouletteStatus.cost_available > 0
                    ? ` · ${rouletteStatus.cost_available} ticket(s)`
                    : ""}
                </div>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <ActionButton
          icon="⚔️"
          title="Battle"
          subtitle="Fight enemies"
          gradient="from-rose-600 to-red-700"
          onClick={() => setScreen("battle")}
        />
        <ActionButton
          icon="🏆"
          title="Top"
          subtitle="Player leaderboard"
          gradient="from-amber-600 to-orange-700"
          onClick={() => setScreen("top")}
        />
        <ActionButton
          icon="📖"
          title="Campaign"
          subtitle="Chapters & bosses"
          gradient="from-yellow-600 to-amber-700"
          onClick={() => setScreen("campaign")}
        />
        <ActionButton
          icon="🛒"
          title="Shop"
          subtitle="Spend gold"
          gradient="from-emerald-600 to-teal-700"
          onClick={() => setScreen("shop")}
        />
        <ActionButton
          icon="👑"
          title="VIP & Pro"
          subtitle="Subscriptions & Elite AI"
          gradient="from-amber-600 to-orange-700"
          onClick={() => setScreen("premium")}
        />
        <ActionButton
          icon="✦"
          title="Astrals"
          subtitle={(hero?.level ?? 0) >= 25 ? "Gather & equip" : "Unlocks at Lv.25"}
          gradient="from-indigo-600 to-violet-700"
          onClick={() => setScreen("astral")}
        />
        <ActionButton
          icon="🤺"
          title="Duels"
          subtitle="PvP 1×1 rooms"
          gradient="from-rose-700 to-pink-800"
          onClick={() => setScreen("duels")}
        />
        <ActionButton
          icon="⚔️"
          title="Arena"
          subtitle={
            partyArenaActive
              ? "Party arena OPEN — join now!"
              : "Ranked PvP"
          }
          gradient="from-purple-600 to-indigo-700"
          onClick={() => setScreen("arena")}
          eventActive={partyArenaActive}
          eventVariant="purple"
        />
        <ActionButton
          icon="🐲"
          title="World Boss"
          subtitle={
            bossActive
              ? "ACTIVE — fight now!"
              : worldBossStatus
                ? formatBossWindowLabel(worldBossStatus)
                : "Scheduled event"
          }
          gradient="from-red-900 to-orange-800"
          onClick={() => setScreen("worldBoss")}
          eventActive={bossActive}
          eventVariant="rose"
        />
        <ActionButton
          icon="🐎"
          title="Mounts"
          subtitle={(hero?.level ?? 0) >= 16 ? "Collect & train mounts" : "Unlocks at Lv.16"}
          gradient="from-stone-600 to-amber-900"
          onClick={() => setScreen("mounts")}
          disabled={(hero?.level ?? 0) < 16}
        />
        <ActionButton
          icon="🕳️"
          title="Catacombs"
          subtitle={(hero?.level ?? 0) >= 23 ? "100 floors · Crypto" : "Unlocks at Lv.23"}
          gradient="from-stone-700 to-zinc-900"
          onClick={() => setScreen("catacombs")}
        />
        <ActionButton
          icon="🔨"
          title="Blacksmith"
          subtitle="Gems · Craft · Legendary"
          gradient="from-orange-800 to-amber-900"
          onClick={() => setScreen("blacksmith")}
        />
        <ActionButton
          icon="🛡"
          title="Guild"
          subtitle={
            guildOverview?.in_guild && guildOverview.my_guild
              ? `${guildOverview.my_guild.name} · ${guildOverview.my_guild.role} · ${guildOverview.my_guild.member_count} members${
                  guildOverview.my_guild.palace !== "none"
                    ? ` · ${palaceLabel(guildOverview.my_guild.palace)}`
                    : ""
                }`
              : guildOverview && !guildOverview.unlocked
                ? `Unlocks at Lv.${guildOverview.unlock_level}`
                : "Create · Find · Battle"
          }
          gradient="from-slate-700 to-blue-900"
          onClick={() => setScreen("guild")}
        />
        <ActionButton
          icon="⛏️"
          title="Amethyst Mine"
          subtitle={
            mineEventActive
              ? "Event active — bonus ore!"
              : (hero?.level ?? 0) >= 10
                ? "Mine · PvP · Trade"
                : "Unlocks at Lv.10"
          }
          gradient="from-violet-800 to-purple-950"
          onClick={() => setScreen("mine")}
          eventActive={mineEventActive}
          eventVariant="violet"
        />
      </div>


      {/* Daily login reward */}
      {showDailyPopup && dailyRewardStatus && (
        <DailyRewardModal
          status={dailyRewardStatus}
          claiming={claimingDaily}
          claimed={dailyClaimed}
          claimSummary={dailyClaimSummary}
          onClaim={handleDailyClaim}
          onClose={closeDailyPopup}
        />
      )}

      {/* Offline income popup */}
      {showIdlePopup && balance && (
        <IdleIncomeModal
          amount={collectedAmount > 0 ? collectedAmount : idleModalPending}
          idle={balance.idle}
          collected={collectedAmount > 0}
          collecting={collecting}
          onCollect={handleCollect}
          onClose={dismissIdleEntryModal}
        />
      )}
    </div>
  );
}

/* ─── Offline income modal ──────────────────────────────────────────────── */
function IdleIncomeModal({
  amount,
  idle,
  collected,
  collecting,
  onCollect,
  onClose,
}: {
  amount: number;
  idle: IdleStatus;
  collected: boolean;
  collecting: boolean;
  onCollect: () => void;
  onClose: () => void;
}) {
  const maxHours = Math.round(idle.max_seconds / 3600);
  const awayLabel = formatDurationLong(idle.offline_seconds);
  const accruedLabel = formatDuration(idle.accrued_seconds);
  const maxLabel = formatDuration(idle.max_seconds);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6">
      <div className="w-full max-w-sm rounded-2xl bg-kingdom-purple border-2 border-gold-500/60 p-6 text-center space-y-4 animate-bounce-in shadow-[0_0_40px_rgba(251,191,36,0.2)]">
        <div className="text-6xl">{collected ? "✅" : "🪙"}</div>

        <div>
          <h2 className="text-xl font-bold font-game text-gold-400">
            {collected ? "Gold Collected!" : "Offline Income"}
          </h2>
          {!collected && (
            <p className="text-gray-400 text-sm mt-2 leading-relaxed">
              You were away for <span className="text-white font-semibold">{awayLabel}</span>
              {idle.is_capped ? (
                <>
                  <br />
                  Income accrued for <span className="text-gold-300 font-semibold">{maxLabel}</span> (max)
                </>
              ) : (
                <>
                  <br />
                  Income accrued for <span className="text-gold-300 font-semibold">{accruedLabel}</span>
                </>
              )}
            </p>
          )}
          {collected && (
            <p className="text-gray-400 text-sm mt-1">
              Your gold has been added to your balance.
            </p>
          )}
        </div>

        {!collected && (
          <div className="space-y-1.5 text-left">
            <div className="flex justify-between text-xs text-gray-400">
              <span>Offline progress</span>
              <span>
                {accruedLabel} / {maxLabel}
              </span>
            </div>
            <div className="h-3 bg-kingdom-dark rounded-full overflow-hidden border border-kingdom-mid">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  idle.is_capped
                    ? "bg-gradient-to-r from-amber-500 to-gold-400"
                    : "bg-gradient-to-r from-emerald-600 to-gold-500"
                }`}
                style={{ width: `${Math.min(100, idle.fill_pct)}%` }}
              />
            </div>
            {idle.is_capped && (
              <p className="text-amber-300/90 text-xs">
                Storage full ({maxHours}h max). Collect now so new income can keep growing.
              </p>
            )}
          </div>
        )}

        <div className="bg-kingdom-dark rounded-xl py-3 px-6 inline-block">
          <span className="text-3xl font-black text-gold-400">
            <FormattedNumber value={amount} prefix="+" />
          </span>
          <span className="text-gold-500 text-lg ml-1">🪙</span>
        </div>

        {!collected && (
          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-kingdom-light text-gray-400 font-semibold active:scale-95 transition-transform"
            >
              Later
            </button>
            <button
              onClick={onCollect}
              disabled={collecting}
              className="flex-2 flex-[2] py-3 rounded-xl bg-gradient-to-r from-gold-500 to-gold-600 text-black font-bold active:scale-95 transition-transform disabled:opacity-60"
            >
              {collecting ? "Collecting…" : "Collect Gold"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface ActionButtonProps {
  icon: string;
  title: string;
  subtitle: string;
  gradient: string;
  onClick: () => void;
  disabled?: boolean;
  /** Live timed event — orbiting indicator + breathing glow */
  eventActive?: boolean;
  eventVariant?: ActiveEventVariant;
}

function ActionButton({
  icon,
  title,
  subtitle,
  gradient,
  onClick,
  disabled,
  eventActive,
  eventVariant = "rose",
}: ActionButtonProps) {
  return (
    <div className="relative min-w-0">
      {eventActive && <ActiveEventRing variant={eventVariant} />}
      <button
        onClick={onClick}
        disabled={disabled}
        className={clsx(
          `relative z-0 w-full bg-gradient-to-br ${gradient} rounded-xl p-4 text-left shadow-lg active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed`,
          eventActive && "shadow-[0_0_20px_rgba(255,255,255,0.08)]",
        )}
      >
        <div className="text-3xl mb-1">{icon}</div>
        <div className="font-bold text-white">{title}</div>
        <div className="text-white/70 text-xs">{subtitle}</div>
      </button>
    </div>
  );
}
